import { io, type Socket } from 'socket.io-client'
import type { ServerToClientEvents, ClientToServerEvents } from '~/types/socket'

type TransportSnapshot = {
    initialTransport: string | null
    settledTransport: string | null
    upgradedToWebsocket: boolean
}

export const useGameSocket = () => {
    const socket = useState<Socket<ServerToClientEvents, ClientToServerEvents> | null>('socket', () => null)
    const isConnected = useState('isConnected', () => false)
    const transportSnapshot = useState<TransportSnapshot>('socketTransportSnapshot', () => ({
        initialTransport: null,
        settledTransport: null,
        upgradedToWebsocket: false,
    }))
    const pollingUsageTracked = useState<boolean>('socketPollingUsageTracked', () => false)
    const config = useRuntimeConfig()
    const socketDebugRollout = config.public.socketDebugRollout === true
    const { track } = import.meta.client ? useAnalytics() : { track: () => undefined }

    let settledTransportTimer: ReturnType<typeof globalThis.setTimeout> | null = null

    const getCurrentTransport = () => {
        return (socket.value?.io.engine as { transport?: { name?: string } } | undefined)?.transport?.name || null
    }

    const clearSettledTransportTimer = () => {
        if (settledTransportTimer) {
            clearTimeout(settledTransportTimer)
            settledTransportTimer = null
        }
    }

    const debugLog = (event: string, payload: Record<string, unknown> = {}) => {
        if (!socketDebugRollout) return
        console.log(`[socket-rollout] ${event}`, payload)
    }

    const settleTransport = (reason: string) => {
        const settledTransport = getCurrentTransport()
        transportSnapshot.value = {
            ...transportSnapshot.value,
            settledTransport,
        }

        const payload = {
            reason,
            initialTransport: transportSnapshot.value.initialTransport,
            settledTransport,
            upgradedToWebsocket: transportSnapshot.value.upgradedToWebsocket,
        }

        debugLog('transport_settled', payload)

        if (settledTransport === 'polling' && !pollingUsageTracked.value) {
            track('socket_transport_settled_on_polling', payload)
            pollingUsageTracked.value = true
        }
    }

    const scheduleSettledTransportCheck = (reason: string) => {
        if (!import.meta.client || typeof globalThis.setTimeout !== 'function') return
        clearSettledTransportTimer()
        settledTransportTimer = globalThis.setTimeout(() => settleTransport(reason), 1500)
    }

    const attachEngineListeners = () => {
        const engine = socket.value?.io.engine as {
            on?: (event: string, handler: (...args: any[]) => void) => void
        } | undefined

        if (!engine?.on) return

        engine.on('upgrade', (transport: { name?: string }) => {
            transportSnapshot.value = {
                ...transportSnapshot.value,
                upgradedToWebsocket: transport?.name === 'websocket' || transportSnapshot.value.upgradedToWebsocket,
            }
            debugLog('transport_upgrade', {
                initialTransport: transportSnapshot.value.initialTransport,
                upgradedTo: transport?.name || null,
            })
            scheduleSettledTransportCheck('upgrade')
        })

        engine.on('upgradeError', (error: Error) => {
            debugLog('transport_upgrade_error', {
                initialTransport: transportSnapshot.value.initialTransport,
                message: error.message,
            })
        })
    }

    const connect = () => {
        if (socket.value) return

        socket.value = io({
            path: '/socket.io',
            transports: ['websocket', 'polling'],
        })

        socket.value.on('connect', () => {
            isConnected.value = true
            transportSnapshot.value = {
                initialTransport: getCurrentTransport(),
                settledTransport: null,
                upgradedToWebsocket: false,
            }
            console.log('Connected to socket server')
            debugLog('connect', {
                socketId: socket.value?.id || null,
                transport: transportSnapshot.value.initialTransport,
            })
            attachEngineListeners()
            scheduleSettledTransportCheck('connect')
        })

        socket.value.on('disconnect', (reason) => {
            isConnected.value = false
            clearSettledTransportTimer()
            console.log('Disconnected from socket server')
            debugLog('disconnect', {
                reason,
                transport: getCurrentTransport(),
            })
        })

        socket.value.on('connect_error', (error) => {
            debugLog('connect_error', {
                message: error.message,
                transport: getCurrentTransport(),
            })
        })
    }

    return {
        socket,
        isConnected,
        connect,
        transportSnapshot,
    }
}
